<template>
  <xh-tree-select-with-modal
    v-model:expandedKeys="expandedKeys"
    v-model:loadedKeys="loadedKeys"
    modalTitle="类目"
    v-model:value="modelValue"
    :searchData="fetchTreeData"
    :treeData="treeData"
    :load-data="onLoadData"
    @confirm="handleConfirm"
    :doNotCloseOnKeysNull="props.doNotCloseOnKeysNull"
  />
</template>

<script setup lang="ts">
import { message, type TreeProps } from 'ant-design-vue'
import type { DataNode } from 'ant-design-vue/es/tree'
import { levelOneKeys } from '@/views/productCenter/commodity/constants'
import { debounce, cloneDeep, isArray, omit } from 'lodash-es'
import { aCommodity, aUser } from '@/apis'
import type { IValueType, IValueTypeSingle } from '@/components/types'
import { useStore } from '@/store'
import { useCategory } from '@/views/productCenter/hooks'
const { sBusId } = useStore()

const modelValue = defineModel<string | undefined>('value', { required: true })

defineOptions({
  name: 'xh-choose-category',
})

type TProps = {
  commodityCategoryType?: string
  isAuth?: boolean
  selectableFn?: Function
  doNotCloseOnKeysNull?: boolean
}

const props = withDefaults(defineProps<TProps>(), {
  selectableFn: (level?: number) => level === 5,
})

const emit = defineEmits<{
  (e: 'change', val: string | number | undefined, list: DataNode[] | null): void
  (e: 'getLevel', params: Record<string, string | undefined>): void
}>()

const filterBooks = async () => {
  if (props.commodityCategoryType) {
    return await useCategory('0', props.commodityCategoryType)
  } else {
    return await useCategory('0')
  }
}
let initTreeData: DataNode[] = []
const treeData = ref<DataNode[]>([])
let authCategory: TUser.Bus['ApiQueryCategoryDetail']['res'] = []
const getInitTreeDataByCommodityType = async () => {
  let list = await filterBooks()
  if (props.isAuth && sBusId) {
    await aUser.bus.queryCategoryDetail({ busId: sBusId }).then((res) => {
      authCategory = res
    })
    initTreeData = conversionTreeData(list, false)
  } else {
    initTreeData = conversionTreeData(list, true)
  }
  treeData.value = cloneDeep(initTreeData)
}
getInitTreeDataByCommodityType()

type TResFilter = {
  id?: string
  title?: string
  name?: string
  key?: number
  // commodityType?: number
  level?: number
  status?: number
  child?: TResFilter[]
  isAll?: boolean
}
const getFilterList = (resFilter: TResFilter[], isAll?: boolean) => {
  // isAll true=全部,false=过滤
  return isAll || !props.isAuth || !sBusId ? resFilter : resFilter.filter((item) => authCategory.find((i) => i.categoryId == item.id))
}

const getIsAll = (id?: string, isAll?: boolean) => {
  let auth = authCategory.find((i) => i.categoryId == id)
  return isAll || !props.isAuth ? true : auth ? auth.ifLeaf == 0 : false
}

const conversionTreeData = (data: TCommodity.Category['ApiCategoryList']['res'], isAll: boolean): DataNode[] => {
  const resFilter = data.filter((item) => item.status == 1)
  const union = getFilterList(resFilter, isAll)
  return union.map((item) => ({
    ...item,
    title: item.name,
    key: item.id!,
    // commodityType: item.commodityType,
    selectable: props.selectableFn(item.level),
    // ifLeaf==0 代表非子节点
    isAll: getIsAll(item.id, isAll),
  }))
}

const onLoadData: TreeProps['loadData'] = (treeNode) => {
  return new Promise<void>((resolve) => {
    if (treeNode.dataRef!.children) {
      resolve()
      return
    }
    aCommodity.category.categoryList({ id: levelOneKeys.includes(treeNode.key) ? '0' : (treeNode.key as string) }).then((res) => {
      treeNode.dataRef!.children = conversionTreeData(res, treeNode.dataRef!.isAll)
      treeNode.dataRef!.isLeaf = !treeNode.dataRef!.children.length
      treeData.value = [...treeData.value]
      resolve()
    })
  })
}
const expandedKeys = ref<string[]>([])
const loadedKeys = ref<string[]>([])
const fetchTreeData = debounce((queryName = '') => {
  const result = cloneDeep(initTreeData)
  if (!queryName) {
    treeData.value = result
    expandedKeys.value = []
    loadedKeys.value = []
    return
  }
  let keys: string[] = []
  aCommodity.category.getCategoryListTree({ queryName, commodityType: props.commodityCategoryType }).then((res) => {
    const conversion = (tree: TResFilter[], isAll: boolean): DataNode[] => {
      const resFilter = tree.filter((item) => item.status == 1)
      const union = getFilterList(resFilter, isAll)
      return union.map((item) => {
        const isFilter = getIsAll(item.id, isAll)
        const list = conversion(item.child || [], isFilter)
        keys.push(item.id!)
        return {
          ...omit(item, 'child'),
          title: item.name,
          key: item.id!,
          children: list,
          isLeaf: !list.length,
          // commodityType: String(item.commodityType),
          selectable: props.selectableFn(item.level),
          isAll: isFilter,
        }
      })
    }
    const children = conversion(res, false)
    treeData.value = children
    expandedKeys.value = keys
    // result.forEach((item) => {
    //   const curChild = children.filter((child) => child.commodityType === item.commodityType)
    //   if (!curChild) return
    //   item.children = curChild
    // })
    // let list = result.filter((item) => item.children && item.children.length)
    // if (props.commodityCategoryType) list = list.filter((item) => item.commodityType === props.commodityCategoryType)
    // treeData.value = list
  })
}, 300)

const findPathToValue = (treeData: DataNode[], targetValue: IValueTypeSingle): DataNode[] | null => {
  const path: DataNode[] = []
  const searchNode = (nodes: DataNode[]): boolean => {
    for (const node of nodes) {
      if (node.key === targetValue) {
        path.push(node)
        return true // 找到匹配的节点，返回 true
      }
      if (node.children && node.children.length > 0) {
        if (searchNode(node.children)) {
          path.push(node) // 在子树中找到匹配的节点，将当前节点加入路径
          return true
        }
      }
    }
    return false // 没有找到匹配的节点，返回 false
  }
  if (searchNode(treeData)) {
    return path.reverse() // 反转路径，使其从根节点到目标节点
  }
  return null // 如果没有找到对应的 value，则返回 null
}

const arr = ['oneLevelCategoryId', 'twoLevelCategoryId', 'threeLevelCategoryId', 'fourLevelCategoryId', 'commodityCategoryId']
const handleConfirm = (val: IValueType) => {
  if (isArray(val) || val == undefined) {
    if (props.doNotCloseOnKeysNull) message.error('请选择类目')
    emit('change', undefined, null)
    return false
  }
  const list = findPathToValue(treeData.value, val)
  emit('change', val, list)
  if (list) {
    let params: Record<string, string | undefined> = {}
    arr.forEach((item, index) => {
      params[item] = list[index]?.key.toString()
    })
    emit('getLevel', params)
  }
}

watch(modelValue, (val) => {
  if (!val) {
    let params: Record<string, string | undefined> = {}
    arr.forEach((item, index) => {
      params[item] = undefined
    })
    emit('getLevel', params)
  }
})
</script>
